import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import './BookingDetailPage.css';
import './BookingRefPage.css';

const STATUS_LABELS = {
  confirmed:   'Confirmed',
  checked_in:  'Checked In',
  checked_out: 'Checked Out',
  cancelled:   'Cancelled',
  pending:     'Pending',
};

const STATUS_CLASSES = {
  confirmed:   'status-confirmed',
  checked_in:  'status-checkedin',
  checked_out: 'status-checkedout',
  cancelled:   'status-cancelled',
  pending:     'status-pending',
};

const PAYMENT_LABELS = {
  pending:  'Payment Pending',
  paid:     'Paid',
  refunded: 'Refunded',
  failed:   'Failed',
};

const PAYMENT_CLASSES = {
  pending:  'status-pending',
  paid:     'status-confirmed',
  refunded: 'status-checkedout',
  failed:   'status-cancelled',
};

function nightsBetween(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  const ms = new Date(checkOut) - new Date(checkIn);
  return Math.max(0, Math.round(ms / 86400000));
}

const BookingDetailPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchBookingDetails();
  }, [bookingId, currentUser]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setBooking(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCancellationPolicy = async () => {
    try {
      const response = await axios.get(
        `/api/policies/hotel/${booking.hotel_id}/cancellation`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCancellationPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
      setCancellationPolicy({
        policy_name: 'Standard Cancellation',
        policy_description: 'Free cancellation up to 24 hours before check-in. Cancellations within 24 hours are subject to a one-night charge.'
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleProceedToPayment = () => {
    setShowPaymentModal(true);
  };

  const handleCancelBooking = async () => {
    await fetchCancellationPolicy();
    setShowCancelModal(true);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#ff9800',
      'paid': '#4caf50',
      'confirmed': '#2196f3',
      'checked_in': '#9c27b0',
      'checked_out': '#607d8b',
      'cancelled': '#f44336',
    };
    return colors[status] || '#666';
  };

  const getDisplayBookingStatus = (bookingData) => {
    if (!bookingData) return '';
    const totalPaid = Number(bookingData.total_paid_amount || 0);
    const hasCompletedPayment = bookingData.payment_status === 'paid' || totalPaid > 0;

    if (bookingData.status === 'cancelled') {
      return hasCompletedPayment ? 'Cancelled (Refund Option)' : 'Cancelled';
    }

    return String(bookingData.status || '').replace('_', ' ');
  };

  const hasCompletedPayment = (bookingData) => {
    const totalPaid = Number(bookingData?.total_paid_amount || 0);
    return bookingData?.payment_status === 'paid' || totalPaid > 0;
  };

  const getRefundDisplayValue = (bookingData) => {
    const refundAmount = Number(bookingData?.refund_amount || 0);
    return refundAmount > 0 ? `PKR ${refundAmount.toFixed(2)}` : 'As per cancellation policy';
  };

  if (loading) {
    return (
      <div className="bref-shell">
        <div className="bref-loading">
          <div className="bref-spinner" />
          <p>Loading booking details…</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="bref-shell">
        <div className="bref-error-card">
          <h2>Booking Not Found</h2>
          <button className="bref-btn-secondary" onClick={() => navigate('/my-bookings')}>Back to My Bookings</button>
        </div>
      </div>
    );
  }

  const nights = nightsBetween(booking.check_in_date, booking.check_out_date);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(window.location.href)}&bgcolor=ffffff&color=312e81&margin=8`;
  const statusClass = STATUS_CLASSES[booking.status] || '';
  const statusLabel = STATUS_LABELS[booking.status] || booking.status;
  const paymentClass = PAYMENT_CLASSES[booking.payment_status] || '';
  const paymentLabel = PAYMENT_LABELS[booking.payment_status] || booking.payment_status;
  const guestCount = booking.adults || booking.guest_count || 1;

  return (
    <div className="bref-shell">
      {/* Top action bar */}
      <div className="bref-topbar no-print">
        <button className="bref-back-btn" onClick={() => navigate('/my-bookings')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          My Bookings
        </button>
        <div className="bref-topbar-actions">
          <button className="bref-btn-secondary" onClick={handleCopy}>
            {copied ? (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> Copied!</>
            ) : (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/></svg> Copy Link</>
            )}
          </button>
          <button className="bref-btn-secondary" onClick={() => window.print()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2"/><path d="M6 18H3a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3" stroke="currentColor" strokeWidth="2"/></svg>
            Print
          </button>
          {booking.payment_status === 'pending' && booking.status !== 'cancelled' && (
            <button className="bref-btn-primary" onClick={handleProceedToPayment}>
              💳 Pay Now
            </button>
          )}
          {booking.status !== 'cancelled' && booking.status !== 'checked_out' && booking.payment_status === 'pending' && (
            <button className="bref-btn-danger" onClick={handleCancelBooking}>
              Cancel Booking
            </button>
          )}
        </div>
      </div>

      {/* Digital Check-in Card */}
      <div className="bref-card">
        <div className="bref-card-header">
          <div className="bref-hotel-badge">InnBotics</div>
          <div className="bref-header-right">
            <span className={`bref-status ${statusClass}`}>{statusLabel}</span>
          </div>
        </div>

        <div className="bref-hotel-section">
          <h1 className="bref-hotel-name">{booking.hotel_name}</h1>
          <p className="bref-hotel-location">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
            </svg>
            {[booking.city, booking.address].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="bref-content-grid">
          <div className="bref-details">

            {/* Dates */}
            <div className="bref-dates-row">
              <div className="bref-date-block">
                <span className="bref-field-label">Check-in</span>
                <span className="bref-date-value">{formatDate(booking.check_in_date)}</span>
              </div>
              <div className="bref-date-arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M15 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="bref-nights">{nights} night{nights !== 1 ? 's' : ''}</span>
              </div>
              <div className="bref-date-block">
                <span className="bref-field-label">Check-out</span>
                <span className="bref-date-value">{formatDate(booking.check_out_date)}</span>
              </div>
            </div>

            {/* Meta */}
            <div className="bref-meta-row">
              <div className="bref-meta-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/><path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                <span>{guestCount} Guest{guestCount !== 1 ? 's' : ''}</span>
              </div>
              {booking.rooms && booking.rooms.map((room, i) => (
                <div key={i} className="bref-meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span>Room {room.room_number}{room.room_type ? ` · ${room.room_type}` : ''}</span>
                </div>
              ))}
              {booking.special_requests && (
                <div className="bref-meta-item bref-meta-full">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span>{booking.special_requests}</span>
                </div>
              )}
            </div>

            {/* Payment */}
            <div className="bref-meta-row">
              <div className="bref-meta-item bref-meta-full bdp-payment-row">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/><line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/></svg>
                <span>PKR <strong>{parseFloat(booking.total_amount || 0).toFixed(2)}</strong></span>
                <span className={`bref-status ${paymentClass} bdp-payment-badge`}>{paymentLabel}</span>
              </div>
              {booking.status === 'cancelled' && hasCompletedPayment(booking) && (
                <div className="bref-meta-item bref-meta-full">
                  <span>Refund: <strong>{getRefundDisplayValue(booking)}</strong></span>
                </div>
              )}
            </div>

            {/* Reference & booked date */}
            <div className="bref-booking-id-row">
              <span className="bref-field-label">Booking Reference</span>
              <span className="bref-booking-id">#{booking.booking_id}</span>
            </div>
            <div className="bref-booking-id-row">
              <span className="bref-field-label">Booked on</span>
              <span className="bref-field-value">{formatDate(booking.created_at)}</span>
            </div>

            {booking.hotel_phone && (
              <div className="bref-contact-row">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6 19.79 19.79 0 0 1 1.63 5.04 2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 18l.42-1.08z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <a href={`tel:${booking.hotel_phone}`} className="bref-phone-link">{booking.hotel_phone}</a>
              </div>
            )}
          </div>

          {/* QR */}
          <div className="bref-qr-section">
            <div className="bref-qr-wrap">
              <img src={qrSrc} alt="Booking QR code" className="bref-qr-img" loading="lazy" />
            </div>
            <p className="bref-qr-caption">Scan to view or share</p>
            <p className="bref-qr-sub">Show this at the front desk</p>
          </div>
        </div>

        <div className="bref-card-footer">
          <span>Generated by InnBotics · innbotics.com</span>
          <span className="bref-footer-sep" />
          <span>Booking #{booking.booking_id}</span>
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal
          booking={booking}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => { setShowPaymentModal(false); fetchBookingDetails(); }}
        />
      )}
      {showCancelModal && (
        <CancelModal
          booking={booking}
          policy={cancellationPolicy}
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => { setShowCancelModal(false); fetchBookingDetails(); }}
        />
      )}
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ booking, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [step, setStep] = useState('select-method'); // select-method, enter-details, verify-pin
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryDate: '',
    cvv: '',
    iban: '',
    accountNumber: '',
    bankName: ''
  });
  const [pin, setPin] = useState('');
  const [generatedPin, setGeneratedPin] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleMethodSelect = (method) => {
    setPaymentMethod(method);
    setStep('enter-details');
  };

  const handleSubmitDetails = async () => {
    // Validate details
    if (paymentMethod === 'credit_card') {
      if (!paymentDetails.cardNumber || !paymentDetails.cvv || !paymentDetails.expiryDate) {
        toast.error('Please fill in all card details');
        return;
      }
    } else if (paymentMethod === 'bank_transfer') {
      if (!paymentDetails.iban && !paymentDetails.accountNumber) {
        toast.error('Please provide IBAN or account number');
        return;
      }
    }

    setProcessing(true);
    try {
      // Request PIN via email
      const response = await axios.post(
        `/api/bookings/${booking.booking_id}/request-payment-pin`,
        { payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setGeneratedPin(response.data.dev_pin); // In development, show PIN
        toast.success('PIN sent to your email!');
        setStep('verify-pin');
      }
    } catch (error) {
      console.error('Error requesting PIN:', error);
      toast.error(error.response?.data?.message || 'Failed to send PIN');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyPin = async () => {
    if (pin !== generatedPin && pin.length === 6) {
      // In production, verify against backend
      toast.error('Invalid PIN');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `/api/bookings/${booking.booking_id}/payment`,
        {
          amount: booking.total_amount,
          payment_method: paymentMethod,
          gateway_reference: `txn_${Date.now()}`,
          status: 'captured',
          payment_details: paymentDetails,
          pin: pin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Payment confirmed! Booking is now confirmed.');
        onSuccess();
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.response?.data?.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Complete Payment</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {step === 'select-method' && (
            <div className="payment-methods">
              <h3>Select Payment Method</h3>
              <div className="method-grid">
                <button 
                  className="method-card"
                  onClick={() => handleMethodSelect('credit_card')}
                >
                  <div className="method-icon">💳</div>
                  <div className="method-name">Credit/Debit Card</div>
                </button>
                <button 
                  className="method-card"
                  onClick={() => handleMethodSelect('bank_transfer')}
                >
                  <div className="method-icon">🏦</div>
                  <div className="method-name">Bank Transfer</div>
                </button>
                <button 
                  className="method-card"
                  onClick={() => handleMethodSelect('easypaisa')}
                >
                  <div className="method-icon">📱</div>
                  <div className="method-name">Easypaisa/JazzCash</div>
                </button>
              </div>
            </div>
          )}

          {step === 'enter-details' && paymentMethod === 'credit_card' && (
            <div className="payment-form">
              <h3>Enter Card Details</h3>
              <div className="form-group">
                <label>Cardholder Name</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={paymentDetails.cardholderName}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, cardholderName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Card Number</label>
                <input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  value={paymentDetails.cardNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
                    setPaymentDetails({ ...paymentDetails, cardNumber: value });
                  }}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    maxLength="5"
                    value={paymentDetails.expiryDate}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length >= 2) {
                        value = value.slice(0, 2) + '/' + value.slice(2, 4);
                      }
                      setPaymentDetails({ ...paymentDetails, expiryDate: value });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <input
                    type="password"
                    placeholder="123"
                    maxLength="3"
                    value={paymentDetails.cvv}
                    onChange={(e) => setPaymentDetails({ ...paymentDetails, cvv: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitDetails}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Continue to Verification'}
              </button>
            </div>
          )}

          {step === 'enter-details' && paymentMethod === 'bank_transfer' && (
            <div className="payment-form">
              <h3>Enter Bank Details</h3>
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  placeholder="e.g., Allied Bank, HBL"
                  value={paymentDetails.bankName}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, bankName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>IBAN (International Bank Account Number)</label>
                <input
                  type="text"
                  placeholder="PK36SCBL0000001123456702"
                  value={paymentDetails.iban}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, iban: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="form-group">
                <label>Account Number (Optional)</label>
                <input
                  type="text"
                  placeholder="1234567890"
                  value={paymentDetails.accountNumber}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, accountNumber: e.target.value })}
                />
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitDetails}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Continue to Verification'}
              </button>
            </div>
          )}

          {step === 'enter-details' && paymentMethod === 'easypaisa' && (
            <div className="payment-form">
              <h3>Enter Mobile Wallet Details</h3>
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="text"
                  placeholder="03XX-XXXXXXX"
                  value={paymentDetails.accountNumber}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, accountNumber: e.target.value })}
                />
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitDetails}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Continue to Verification'}
              </button>
            </div>
          )}

          {step === 'verify-pin' && (
            <div className="pin-verification">
              <h3>Verify Your Email</h3>
              <p>We've sent a 6-digit PIN to your email address. Please enter it below to confirm payment.</p>
              {generatedPin && (
                <div className="dev-note">
                  <strong>Development Mode: Your PIN is {generatedPin}</strong>
                </div>
              )}
              <div className="form-group">
                <label>Enter PIN</label>
                <input
                  type="text"
                  placeholder="000000"
                  maxLength="6"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  className="pin-input"
                />
              </div>
              <button 
                className="btn btn-success"
                onClick={handleVerifyPin}
                disabled={processing || pin.length !== 6}
              >
                {processing ? 'Verifying...' : 'Confirm Payment'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setStep('enter-details')}
              >
                Back
              </button>
            </div>
          )}

          <div className="payment-summary">
            <div className="summary-row">
              <span>Total Amount:</span>
              <strong>PKR {parseFloat(booking.total_amount).toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cancel Modal Component
const CancelModal = ({ booking, policy, onClose, onConfirm }) => {
  const { token } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);

  useEffect(() => {
    calculateRefund();
  }, [booking, policy]);

  const calculateRefund = () => {
    const totalAmount = parseFloat(booking.total_amount);
    const checkInDate = new Date(booking.check_in_date);
    const now = new Date();
    const hoursUntilCheckIn = (checkInDate - now) / (1000 * 60 * 60);

    // Default policy: Free cancellation 24hrs before, otherwise 1 night charge
    if (hoursUntilCheckIn > 24) {
      setRefundAmount(totalAmount);
    } else if (hoursUntilCheckIn > 0) {
      // Charge one night (assume equal distribution across nights)
      const nights = Math.ceil((new Date(booking.check_out_date) - checkInDate) / (1000 * 60 * 60 * 24));
      const perNight = totalAmount / nights;
      setRefundAmount(totalAmount - perNight);
    } else {
      setRefundAmount(0);
    }
  };

  const handleConfirmCancel = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `/api/bookings/${booking.booking_id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Booking cancelled successfully');
        onConfirm();
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cancel Booking</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="warning-box">
            <h3>⚠️ Are you sure you want to cancel?</h3>
          </div>

          {policy && (
            <div className="policy-section">
              <h4>Cancellation Policy: {policy.policy_name}</h4>
              <p>{policy.policy_description}</p>
            </div>
          )}

          <div className="refund-calculation">
            <h4>Refund Breakdown</h4>
            <div className="refund-row">
              <span>Booking Amount:</span>
              <span>PKR {parseFloat(booking.total_amount).toFixed(2)}</span>
            </div>
            <div className="refund-row">
              <span>Cancellation Fee:</span>
              <span className="fee">
                - PKR {(parseFloat(booking.total_amount) - refundAmount).toFixed(2)}
              </span>
            </div>
            <div className="refund-row total">
              <span>Refund Amount:</span>
              <strong>PKR {refundAmount.toFixed(2)}</strong>
            </div>
          </div>

          <div className="modal-actions">
            <button 
              className="btn btn-secondary"
              onClick={onClose}
            >
              Keep Booking
            </button>
            <button 
              className="btn btn-danger"
              onClick={handleConfirmCancel}
              disabled={processing}
            >
              {processing ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
